<template>
  <TitleBar :workOrder="workOrder"/>
  <template v-if="workOrder.status.name === 'APPROVED'">
    Werkorder is goedgekeurd en kan niet meer worden aangepast.
  </template>
  <template v-else>
    <v-col sm="12">
      <h2 class="subtitle h-25px mt-0 mb-4">
        BRL-9101 checklist
      </h2>

      <template v-if="loading">
        Laden...
      </template>

      <template v-else-if="hasExistingSubmissions && !overrideExistingSubmission">
        <v-alert
          type="success"
          variant="tonal"
          class="mb-4"
        >
          Er is al een BRL-9101 checklist ingevuld voor deze werkorder.
        </v-alert>
        <v-btn
          color="primary"
          @click="overrideExistingSubmission = true"
        >
          Toch een nieuwe checklist invullen
        </v-btn>
      </template>

      <template v-else-if="sections.length">
        <v-stepper
          v-model="currentStep"
          :items="stepLabels"
          flat
          hide-actions
          class="brl-stepper mb-4"
        />

        <v-card flat>
          <v-table class="brl-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Ja / Nee / nvt</th>
                <th>Opmerkingen</th>
                <th>Afwijking</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="question in currentSection.questions"
                :key="question.id"
                :class="{ 'row-invalid': showValidation && !answers[question.id].value }"
              >
                <td>{{ question.question }}</td>
                <td>
                  <v-radio-group
                    v-model="answers[question.id].value"
                    class="answer-radio-group"
                    inline
                    hide-details
                    density="compact"
                    :error="showValidation && !answers[question.id].value"
                  >
                    <v-radio label="Ja" value="ja" />
                    <v-radio label="Nee" value="nee" />
                    <v-radio label="nvt" value="nvt" />
                  </v-radio-group>
                </td>
                <td>
                  <v-textarea
                    v-model="answers[question.id].comment"
                    placeholder="Opmerkingen"
                    hide-details
                    density="compact"
                    variant="underlined"
                    auto-grow
                    rows="1"
                    row-height="24"
                  />
                </td>
                <td>
                  <v-textarea
                    v-model="answers[question.id].deviation"
                    placeholder="Afwijking"
                    hide-details
                    density="compact"
                    variant="underlined"
                    auto-grow
                    rows="1"
                    row-height="24"
                  />
                </td>
              </tr>
            </tbody>
          </v-table>

          <v-alert
            v-if="showValidation && !currentSectionValid"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-3 mt-3"
          >
            Beantwoord alle vragen voordat je verder gaat.
          </v-alert>

          <div class="d-flex justify-space-between pa-3">
            <v-btn
              :disabled="isFirstStep"
              @click="previousStep"
            >
              Vorige stap
            </v-btn>
            <v-btn
              v-if="!isLastStep"
              color="primary"
              @click="nextStep"
            >
              Volgende stap
            </v-btn>
            <v-btn
              v-else
              color="primary"
              @click="submitChecklist"
            >
              Checklist opslaan en werkorder sluiten
            </v-btn>
          </div>
        </v-card>
      </template>
    </v-col>
  </template>
</template>

<script>
import { getBrlChecklist, submitBrlChecklist } from '@/api/brlChecklists'
import TitleBar from '@/components/WorkOrders/TitleBar'
import { closeWorkOrder } from '@/api/workOrders'

export default {
  components: {
    TitleBar,
  },

  props: {
    workOrder: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      brlChecklist: null,
      answers: {},
      currentStep: 1,
      loading: true,
      submitting: false,
      validationTriggered: {},
      overrideExistingSubmission: false,
    }
  },

  computed: {
    sections() {
      return this.brlChecklist?.sections ?? []
    },

    stepLabels() {
      return this.sections.map(section => section.name)
    },

    currentSection() {
      return this.sections[this.currentStep - 1] ?? { questions: [] }
    },

    isFirstStep() {
      return this.currentStep <= 1
    },

    isLastStep() {
      return this.currentStep >= this.sections.length
    },

    currentSectionValid() {
      return this.currentSection.questions.every(
        question => !!this.answers[question.id]?.value
      )
    },

    allSectionsValid() {
      return this.sections.every(section =>
        section.questions.every(question => !!this.answers[question.id]?.value)
      )
    },

    showValidation() {
      return !!this.validationTriggered[this.currentStep]
    },

    hasExistingSubmissions() {
      return !!(this.workOrder.brlChecklistSubmissions && this.workOrder.brlChecklistSubmissions.length > 0)
    },
  },

  created() {
    this.fetchBrlChecklist()
  },

  methods: {
    async fetchBrlChecklist() {
      try {
        const response = await getBrlChecklist('1.0')

        this.brlChecklist = response.data
        this.initAnswers()
      } catch (error) {
        alert('Failed to fetch BRL checklist: ', error)
      } finally {
        this.loading = false
      }
    },

    initAnswers() {
      const answers = {}
      for (const section of this.sections) {
        for (const question of section.questions) {
          answers[question.id] = {
            value: null,
            comment: '',
            deviation: '',
          }
        }
      }
      this.answers = answers
    },

    nextStep() {
      if (this.isLastStep) {
        return
      }
      if (!this.currentSectionValid) {
        this.validationTriggered[this.currentStep] = true
        return
      }
      this.validationTriggered[this.currentStep] = false
      this.currentStep += 1
    },

    previousStep() {
      if (!this.isFirstStep) {
        this.currentStep -= 1
      }
    },

    mapAnswerValue(value) {
      if (value === 'ja') {
        return true
      }
      if (value === 'nee') {
        return false
      }
      if (value === 'nvt') {
        return null
      }

      return null
    },

    buildChecklistPayload() {
      const sections = this.sections.map(section => ({
        section_id: section.id,
        questions: section.questions.map(question => ({
          question_id: question.id,
          answer: this.mapAnswerValue(this.answers[question.id]?.value),
          comment: this.answers[question.id]?.comment || '',
          deviation: this.answers[question.id]?.deviation || '',
        })),
      }))

      return {answers: sections}
    },

    async submitChecklist() {
      if (!this.allSectionsValid) {
        for (let i = 0; i < this.sections.length; i += 1) {
          const section = this.sections[i]
          const valid = section.questions.every(
            question => !!this.answers[question.id]?.value
          )
          if (!valid) {
            this.validationTriggered[i + 1] = true
            this.currentStep = i + 1
            return
          }
        }
        return
      }

      if (this.submitting) {
        return
      }

      this.submitting = true

      try {
        const checklistData = this.buildChecklistPayload()

        await submitBrlChecklist(
          this.workOrder.id,
          this.brlChecklist.version,
          checklistData
        )

        await this.$store.dispatch('workOrders.fetchSingle', this.workOrder.id)

        this.executeCloseWorkOrder()

        this.$router.push({
          name: 'WorkOrdersViewOverview',
          params: { id: this.workOrder.id },
        })
      } catch (error) {
        alert('Failed to submit BRL checklist: ', error)
      } finally {
        this.submitting = false
      }
    },

    executeCloseWorkOrder() {
      closeWorkOrder(this.workOrder.id).then(({ data }) => {
        this.$store.commit('workOrders.setView', { ...this.workOrder, status: data.status })
      }).catch(error => Say('error', error))
    },
  },
}
</script>

<style lang="scss" scoped>
.brl-stepper {
  background: transparent;

  :deep(.v-stepper-item__title) {
    display: none;
  }
  :deep(.v-stepper-window) {
    display: none;
  }
}

.brl-table {
  font-size: 14px !important;
  :deep(label),
  :deep(textarea) {
    font-size: 14px !important;
  }

  :deep(table) {
    table-layout: auto;
    width: 100%;
  }

  :deep(th:nth-child(1)),
  :deep(td:nth-child(1)) {
    min-width: 100px;
    word-break: break-word;
  }

  :deep(th:nth-child(2)),
  :deep(td:nth-child(2)) {
    width: 200px;
  }

  th {
    font-weight: 500;
  }

  tr.row-invalid {
    background-color: rgba(244, 67, 54, 0.06);
  }
}

.answer-radio-group {
  :deep(.v-selection-control-group) {
    flex-wrap: nowrap;
  }

  :deep(.v-selection-control) {
    margin-inline-end: 12px;
  }

  :deep(.v-label) {
    white-space: nowrap;
  }
}

@media (max-width: 699px) {

  .brl-table {
    :deep(th:nth-child(2)),
    :deep(td:nth-child(2)) {
      width: 120px;
    }
  }

  .answer-radio-group {
    margin-top: 8px;
    margin-bottom: 8px;

    :deep(.v-selection-control-group) {
      flex-direction: column;
      align-items: flex-start;
    }

    :deep(.v-selection-control) {
      margin-inline-end: 0;
      margin-bottom: 6px;
    }
  }
}
</style>
